import React from "react";
import { Link } from "react-router-dom";
import { Phone, MessageCircle, Handshake, MapPin, BadgeCheck, Shield } from "lucide-react";
import ComplaintsBookButton from "@/components/layout/ComplaintsBookButton";
import { getStartStrings } from "@/components/landing/orthocareStartI18n";
import { isGestosAgeisDomain } from "@/lib/siteDomain";
import { useStoreInfo } from "@/hooks/useStoreInfo";

const ORTHOCARE_LOGO =
  "https://media.base44.com/images/public/691220492173ae6cbe68ab07/eec4f6188_file_000000000f3871f4acdc11aca9b1a76e.png";
const GESTOS_LOGO =
  "https://media.base44.com/images/public/691220492173ae6cbe68ab07/93a4b2fb8_1777227440051110206008173168802.png";

/**
 * Rodapé oficial da Orthocare Rent — usado na página /start e em todas as
 * páginas públicas da Orthocare. Inclui a secção de suporte 24h, as colunas
 * (marca, serviços, legal, contacto) e o copyright.
 */
// Extra footer strings not covered by the shared start-page dictionary. Kept
// here so no English leaks into the ES/FR/DE/IT landing pages, where this
// footer is rendered.
const FOOTER_EXTRA = {
  pt: { connectAI: 'Ligar Assistente de IA', partners: 'Programa de Parceiros', certified: 'Equipamentos certificados CE', hygiene: 'Higienização hospitalar', crossGestos: 'Serviço oficial de aluguer expresso de mobilidade assegurado pela OrthoCare Rent.', crossOrtho: 'Serviço de Mobilidade Expresso integrado na rede de apoio ortopédico Gestos Ágeis.' },
  en: { connectAI: 'Connect AI Assistant', partners: 'Partners Program', certified: 'CE-certified equipment', hygiene: 'Hospital hygiene', crossGestos: 'Official express mobility rental service provided by OrthoCare Rent.', crossOrtho: 'Express Mobility Service integrated into the Gestos Ágeis orthopedic support network.' },
  es: { connectAI: 'Conectar Asistente de IA', partners: 'Programa de Colaboradores', certified: 'Equipos certificados CE', hygiene: 'Higienización hospitalaria', crossGestos: 'Servicio oficial de alquiler exprés de movilidad prestado por OrthoCare Rent.', crossOrtho: 'Servicio de Movilidad Exprés integrado en la red de apoyo ortopédico Gestos Ágeis.' },
  fr: { connectAI: "Connecter l'assistant IA", partners: 'Programme Partenaires', certified: 'Matériel certifié CE', hygiene: 'Hygiène hospitalière', crossGestos: 'Service officiel de location express de matériel de mobilité assuré par OrthoCare Rent.', crossOrtho: 'Service de Mobilité Express intégré au réseau de soutien orthopédique Gestos Ágeis.' },
  de: { connectAI: 'KI-Assistent verbinden', partners: 'Partnerprogramm', certified: 'CE-zertifizierte Geräte', hygiene: 'Krankenhaushygiene', crossGestos: 'Offizieller Express-Mobilitätsverleih von OrthoCare Rent.', crossOrtho: 'Express-Mobilitätsservice im orthopädischen Unterstützungsnetz von Gestos Ágeis.' },
  it: { connectAI: "Collega l'assistente IA", partners: 'Programma Partner', certified: 'Attrezzature certificate CE', hygiene: 'Igiene ospedaliera', crossGestos: 'Servizio ufficiale di noleggio express di mobilità fornito da OrthoCare Rent.', crossOrtho: 'Servizio di Mobilità Express integrato nella rete di supporto ortopedico Gestos Ágeis.' },
};

export default function OrthocareFooter({ language = "pt" }) {
  const S = getStartStrings(language);
  const X = FOOTER_EXTRA[language] || FOOTER_EXTRA.en;
  const isGestos = isGestosAgeisDomain();
  const { data: stores = [] } = useStoreInfo();
  const junqueira = stores.find((s) => s.name?.toLowerCase().includes("junqueira")) || stores[0];
  const logoUrl = isGestos ? GESTOS_LOGO : ORTHOCARE_LOGO;
  const logoAlt = isGestos ? "Gestos Ágeis" : "Orthocare";
  const logoStyle = isGestos ? { filter: "brightness(0) invert(1)" } : undefined;
  const leadingOrtho = isGestos
    ? "Serviço de aluguer de equipamentos de mobilidade líder."
    : S.leadingOrtho;
  const copyright = isGestos
    ? "© 2026 Gestos Ágeis. Todos os direitos reservados."
    : S.copyright;
  const brandOwned = isGestos
    ? "Gestos Ágeis Unipessoal Lda – NIF: 515948039"
    : S.brandOwned;
  const social = isGestos
    ? {
        fb: "https://www.facebook.com/gestosageis",
        ig: "https://www.instagram.com/gestosageis",
        li: "https://www.linkedin.com/company/gestosageis",
      }
    : {
        fb: "https://www.facebook.com/orthocare.rent",
        ig: "https://www.instagram.com/orthocare.rent",
        li: "https://www.linkedin.com/company/orthocare-rent",
      };
  // Domain-aware support-section colors: Gestos Ágeis green on gestosageis.pt,
  // standard blue on orthocare.pt (unchanged).
  const statusDot = isGestos ? "bg-[#0e6b4f]" : "bg-blue-400";
  const callIconBg = isGestos ? "bg-[#0e6b4f]" : "bg-blue-600";
  const waCard = isGestos
    ? "bg-[#0e6b4f] hover:bg-[#0a4a37]"
    : "bg-blue-600 hover:bg-blue-700";

  return (
    <>
      {/* ── Contact / Support 24h ─────────────────────────────── */}
      <section className="bg-gray-950 py-8 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white text-xs font-semibold px-3 py-1.5 rounded-full mb-3">
            <span className={`w-2 h-2 rounded-full ${statusDot} animate-pulse`} />
            {S.support24}
          </span>
          <p className="text-gray-400 text-xs mb-5">{S.available24}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-sm mx-auto">
            <a href="tel:+351216035281" className="flex-1 bg-gray-800 hover:bg-gray-700 transition rounded-xl p-4 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full ${callIconBg} flex items-center justify-center shrink-0`}>
                <Phone className="w-5 h-5 text-white" />
              </div>
              <div className="text-left">
                <p className="text-gray-400 text-xs">{S.callNow}</p>
                <p className="text-white font-bold text-sm">+351 216 035 281</p>
              </div>
            </a>
            <a
              href="https://wa.me/351216035281"
              target="_blank"
              rel="noopener noreferrer"
              className={`flex-1 ${waCard} transition rounded-xl p-4 flex items-center gap-3`}>
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <div className="text-left">
                <p className="text-white/70 text-xs">WhatsApp 24h</p>
                <p className="text-white font-bold text-sm">{S.sendMsg}</p>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* ── Footer ────────────────────────────────────────────── */}
      <footer className="bg-gray-900 text-gray-300 py-8 px-4">
        <div className="max-w-7xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-6 mb-6">
          <div className="col-span-2 sm:col-span-1 min-w-0 overflow-hidden">
            <img
              src={logoUrl}
              alt={logoAlt}
              width={220}
              height={80}
              loading="lazy"
              style={logoStyle}
              className="h-12 sm:h-14 max-w-[120px] sm:max-w-[130px] w-auto object-contain rounded-lg drop-shadow-xl mb-2" />
            <p className="text-xs text-gray-400">{leadingOrtho}</p>
            <a href="/faq" className="mt-2 inline-block text-[#cebe75] text-xs hover:opacity-80 transition-colors">
              {S.faqLink}
            </a>
            <a href="/about" className="mt-1 inline-block text-[#cebe75] text-xs hover:opacity-80 transition-colors">
              {S.aboutLink}
            </a>
            <a href="/contact" className="mt-1 inline-block text-[#cebe75] text-xs hover:opacity-80 transition-colors">
              {S.contactLink}
            </a>
            <Link to="/connect" className="mt-1 inline-block text-[#cebe75] text-xs hover:opacity-80 transition-colors">
              {X.connectAI}
            </Link>
          </div>
          <div>
            <h3 className="text-white font-bold mb-2 text-sm">{S.services}</h3>
            <ul className="space-y-1 text-xs text-gray-400">
              <li>{S.equipmentRental}</li>
              <li>{S.deliveryPickup}</li>
              <li>{S.support}</li>
            </ul>
          </div>
          <div>
            <h3 className="text-white font-bold mb-2 text-sm">{S.legal}</h3>
            <ul className="space-y-1 text-xs text-gray-400">
              <li><Link to="/privacy" className="hover:text-white transition-colors">{S.privacy}</Link></li>
              <li><Link to="/terms" className="hover:text-white transition-colors">{S.terms}</Link></li>
              <li><Link to="/cookies" className="hover:text-white transition-colors">{S.cookies}</Link></li>
            </ul>
            <div className="mt-3">
              <ComplaintsBookButton height={40} />
            </div>
          </div>
          <div>
            <h3 className="text-white font-bold mb-2 text-sm">{S.contact}</h3>
            <p className="text-xs text-gray-400">geral@gestosageis.com</p>
            <p className="text-xs text-gray-400 mt-1">+351 216 035 281</p>
            {junqueira && (
              <>
                <p className="text-xs text-gray-400 mt-2">{junqueira.address}, {junqueira.postal_code} {junqueira.county || 'Lisboa'}</p>
                {junqueira.opening_hours && (
                  <p className="text-xs text-gray-400 mt-1">{junqueira.opening_hours}</p>
                )}
              </>
            )}
            {!isGestos && (
            <Link
              to="/parceiros"
              className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#cebe75] text-[#0a3d62] text-xs font-bold hover:brightness-110 transition shadow-sm">
              <Handshake className="w-3.5 h-3.5" />
              {X.partners}
            </Link>
            )}
            <div className="flex gap-2 mt-3">
              <a href={social.fb} target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition" aria-label="Facebook">
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>
              </a>
              <a href={social.ig} target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition" aria-label="Instagram">
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" /></svg>
              </a>
              <a href={social.li} target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition" aria-label="LinkedIn">
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z" /></svg>
              </a>
            </div>
          </div>
        </div>
        {/* E-E-A-T: NIF, certificações e lojas físicas com Google Maps */}
        <div className="border-t border-gray-700 pt-4">
          <div className="flex flex-wrap items-center justify-center gap-3 mb-3 text-xs text-gray-400">
            <span className="inline-flex items-center gap-1">
              <BadgeCheck className="w-3.5 h-3.5 text-green-400" />
              {isGestos ? 'NIF: 515948039' : 'NIF: 515948039'}
            </span>
            <span className="inline-flex items-center gap-1">
              <Shield className="w-3.5 h-3.5 text-blue-400" />
              {X.certified}
            </span>
            <span className="inline-flex items-center gap-1">
              <Shield className="w-3.5 h-3.5 text-green-400" />
              {X.hygiene}
            </span>
          </div>
          {junqueira && (
            <div className="flex flex-wrap justify-center gap-4 mb-3 text-xs text-gray-400">
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${junqueira.address}, ${junqueira.postal_code || ''} Lisboa`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 hover:text-white transition-colors">
                <MapPin className="w-3.5 h-3.5 text-red-400" />
                {junqueira.address}, {junqueira.postal_code || '1300-343'} Lisboa
              </a>
              <a
                href="https://www.google.com/maps/search/?api=1&query=Estrada+de+Benfica+739A+1500-089+Lisboa"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 hover:text-white transition-colors">
                <MapPin className="w-3.5 h-3.5 text-red-400" />
                Estrada de Benfica 739A, 1500-089 Lisboa
              </a>
            </div>
          )}
          {/* Cross-Brand Trust: Link Equity entre OrthoCare e Gestos Ágeis */}
          <div className="text-center text-xs text-gray-400 mb-3 px-4 max-w-2xl mx-auto">
            {isGestos ? X.crossGestos : X.crossOrtho}
          </div>
          <div className="text-center text-xs text-gray-500">
            {copyright}
            <p className="mt-1">{brandOwned}</p>
          </div>
        </div>
      </footer>
    </>
  );
}